import { useState, useEffect, useCallback, useRef } from 'react';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-master/components/ui/select';
import {
  RefreshCw, Play, Pause, Trash2, RotateCcw, AlertTriangle, CheckCircle2,
  XCircle, Clock, Activity, Zap, Server, ChevronDown, ChevronUp, Loader2,
  ListX, SkipForward, Info, X
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import ioClient, { Socket } from 'socket.io-client';
import { getSocketUrl, getSocketConfig } from '@easyflow/ui-frontend';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';

interface QueueStatus {
  name: string;
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused: number;
  };
  isPaused: boolean;
  total: number;
  error?: string;
}

interface QueueJob {
  id: string;
  name: string;
  data: {
    tenantId: string;
    channel?: string;
    campaignId?: string;
    to?: string;
    leadName?: string;
    type?: string;
  };
  status: string;
  attempts: number;
  maxAttempts: number;
  timestamp: number;
  processedOn?: number;
  finishedOn?: number;
  failedReason?: string;
  delay?: number;
  progress?: number;
}

interface HistoryPoint {
  time: string;
  label: string;
  completed: number;
  failed: number;
}

const QUEUE_LABELS: Record<string, string> = {
  'workflows': 'Workflow Automazioni',
  'mass-outreach': 'Invii Massivi SMS/WA',
  'messaging': 'Messaggi Singoli',
};

const STATUS_COLORS: Record<string, string> = {
  waiting: 'bg-yellow-100 text-yellow-800',
  active: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  delayed: 'bg-purple-100 text-purple-800',
  paused: 'bg-muted text-foreground',
};

export default function QueueMonitor() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [queues, setQueues] = useState<QueueStatus[]>([]);
  const [redisInfo, setRedisInfo] = useState<{ host: string | null; port: number | null; latencyMs: number | null }>({ host: null, port: null, latencyMs: null });
  const [selectedQueue, setSelectedQueue] = useState('mass-outreach');
  const [selectedStatus, setSelectedStatus] = useState('failed');
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [tenants, setTenants] = useState<string[]>([]);
  const [tenantFilter, setTenantFilter] = useState('all');
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [allHistory, setAllHistory] = useState<Record<string, HistoryPoint[]>>({});
  const [historyHours, setHistoryHours] = useState('168');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const socketRef = useRef<Socket | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get<{ connected?: boolean; queues?: QueueStatus[]; redis?: { host?: string | null; port?: number | null; latencyMs?: number | null } }>('/master/queue-monitor/status');
      if (res?.success) {
        setIsConnected(Boolean(res.data?.connected));
        setQueues(res.data?.queues || []);
        if (res.data?.redis) {
          setRedisInfo({
            host: res.data.redis.host ?? null,
            port: res.data.redis.port ?? null,
            latencyMs: typeof res.data.redis.latencyMs === 'number' ? res.data.redis.latencyMs : null,
          });
        }
      }
    } catch (err) {
      console.error('Queue status error:', err);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ status: selectedStatus, limit: '50' });
      if (tenantFilter !== 'all') qs.set('tenant', tenantFilter);
      if (dateFrom) qs.set('dateFrom', new Date(dateFrom).getTime().toString());
      if (dateTo) qs.set('dateTo', new Date(dateTo + 'T23:59:59').getTime().toString());
      const res = await apiClient.get<{ jobs?: QueueJob[]; total?: number }>(`/master/queue-monitor/jobs/${selectedQueue}?${qs}`);
      if (res?.success) {
        setJobs(res.data?.jobs || []);
        setJobsTotal(res.data?.total || 0);
      }
    } catch (err) {
      console.error('Queue jobs error:', err);
    }
  }, [selectedQueue, selectedStatus, tenantFilter, dateFrom, dateTo]);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await apiClient.get<string[]>('/master/queue-monitor/tenants');
      if (res?.success) {
        setTenants(res.data || []);
      }
    } catch {}
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await apiClient.get<HistoryPoint[]>(`/master/queue-monitor/history/${selectedQueue}?hours=${historyHours}`);
      if (res?.success) {
        setHistory(res.data || []);
      }
    } catch {}
  }, [selectedQueue, historyHours]);

  const fetchAllHistory = useCallback(async () => {
    try {
      const queueNames = ['workflows', 'mass-outreach', 'messaging'];
      const results: Record<string, HistoryPoint[]> = {};
      await Promise.all(queueNames.map(async (qName) => {
        try {
          const res = await apiClient.get<HistoryPoint[]>(`/master/queue-monitor/history/${qName}?hours=${historyHours}`);
          if (res?.success) {
            results[qName] = res.data || [];
          }
        } catch {
          results[qName] = [];
        }
      }));
      setAllHistory(results);
    } catch {}
  }, [historyHours]);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchStatus(), fetchJobs(), fetchTenants(), fetchHistory(), fetchAllHistory()]);
    setIsLoading(false);
  }, [fetchStatus, fetchJobs, fetchTenants, fetchHistory, fetchAllHistory]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    if (!token) return;

    // Task #2569 — config Socket.IO condivisa (riconnessione infinita + jitter).
    const socket = ioClient(getSocketUrl(), {
      ...getSocketConfig(),
      auth: { token },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setSocketConnected(true);
      socket.emit('queue:subscribe');
    });
    socket.on('disconnect', () => setSocketConnected(false));
    socket.on('queue:update', (data: { queues?: QueueStatus[] }) => {
      if (data?.queues) setQueues(data.queues);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const intervalMs = socketConnected ? 60000 : 10000;
    const iv = setInterval(() => {
      void fetchStatus();
      void fetchJobs();
    }, intervalMs);
    return () => clearInterval(iv);
  }, [autoRefresh, fetchStatus, fetchJobs, socketConnected]);

  useEffect(() => { void fetchJobs(); }, [selectedQueue, selectedStatus, tenantFilter, dateFrom, dateTo, fetchJobs]);
  useEffect(() => { void fetchHistory(); }, [selectedQueue, historyHours, fetchHistory]);
  useEffect(() => { void fetchAllHistory(); }, [historyHours, fetchAllHistory]);

  const executeAction = async (queueName: string, action: string, jobId?: string) => {
    const key = `${queueName}-${action}-${jobId || ''}`;
    setActionLoading(key);
    try {
      const res = await apiClient.post<unknown>(`/master/queue-monitor/action/${queueName}`, { action, jobId });
      if (res?.success) {
        toast({ title: 'Azione eseguita', description: res.message });
        await Promise.all([fetchStatus(), fetchJobs()]);
      } else {
        toast({ title: 'Errore', description: res?.error, variant: 'destructive' });
      }
    } catch (err: unknown) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const getQueueHealth = (q: QueueStatus) => {
    if (q.error) return { color: 'text-muted-foreground/70', label: 'Errore', icon: XCircle };
    if (q.isPaused) return { color: 'text-yellow-500', label: 'In Pausa', icon: Pause };
    if (q.counts.failed > 10) return { color: 'text-red-500', label: 'Critico', icon: AlertTriangle };
    if (q.counts.failed > 0) return { color: 'text-orange-500', label: 'Attenzione', icon: AlertTriangle };
    if (q.counts.active > 0) return { color: 'text-blue-500', label: 'Attivo', icon: Activity };
    return { color: 'text-green-500', label: 'OK', icon: CheckCircle2 };
  };

  const totalFailed = queues.reduce((s, q) => s + (q.counts?.failed || 0), 0);
  const totalActive = queues.reduce((s, q) => s + (q.counts?.active || 0), 0);
  const totalWaiting = queues.reduce((s, q) => s + (q.counts?.waiting || 0), 0);
  const totalCompleted = queues.reduce((s, q) => s + (q.counts?.completed || 0), 0);

  if (!isConnected && !isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 flex items-start gap-4">
          <AlertTriangle className="h-6 w-6 text-yellow-500 mt-1" />
          <div>
            <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">Redis @ REDIS-1 (10.4.202.57) non raggiungibile</h3>
            <p className="text-sm text-yellow-600 dark:text-yellow-300 mt-1">
              Le code BullMQ richiedono il nodo Redis dedicato <span className="font-mono">REDIS-1 (10.4.202.57:6379)</span>.
              Verificare <span className="font-mono">REDIS_URL</span> sui backend BE-1/BE-2 e lo stato del servizio su REDIS-1.
              Il sistema sta usando la modalità in-memory come fallback. Runbook: <span className="font-mono">docs/runbooks/redis.md</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Code BullMQ — Redis @ REDIS-1 (10.4.202.57)</h2>
          <Badge variant={isConnected ? 'default' : 'destructive'} className={isConnected ? 'bg-green-100 text-green-800' : ''}>
            {isConnected ? 'Redis Connesso' : 'Disconnesso'}
          </Badge>
          {isConnected && (redisInfo.host || redisInfo.latencyMs !== null) && (
            <Badge variant="outline" className="text-xs font-mono">
              {redisInfo.host ?? 'host?'}{redisInfo.port ? `:${redisInfo.port}` : ''}
              {redisInfo.latencyMs !== null ? ` · ${redisInfo.latencyMs}ms` : ''}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'text-green-600 border-green-300' : 'text-muted-foreground/70'}>
            {autoRefresh ? <Play className="h-3 w-3 mr-1" /> : <Pause className="h-3 w-3 mr-1" />}
            {autoRefresh ? 'Auto' : 'Pausa'}
          </Button>
          <Button variant="outline" size="sm" onClick={loadAll} disabled={isLoading}>
            <RefreshCw className={`h-3 w-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GlassCard>
          <GlassCardContent className="p-4">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-blue-600">{totalActive}</p>
                <p className="text-xs text-muted-foreground">In Esecuzione</p>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
        <GlassCard>
          <GlassCardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold text-yellow-600">{totalWaiting}</p>
                <p className="text-xs text-muted-foreground">In Attesa</p>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
        <GlassCard>
          <GlassCardContent className="p-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-red-600">{totalFailed}</p>
                <p className="text-xs text-muted-foreground">Falliti</p>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
        <GlassCard>
          <GlassCardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-600">{totalCompleted}</p>
                <p className="text-xs text-muted-foreground">Completati</p>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {queues.map(q => {
          const health = getQueueHealth(q);
          const HealthIcon = health.icon;
          return (
            <GlassCard key={q.name} className={`cursor-pointer transition-all ${selectedQueue === q.name ? 'ring-2 ring-blue-400' : ''}`}
              onClick={() => setSelectedQueue(q.name)}>
              <GlassCardHeader className="pb-2">
                <GlassCardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    {QUEUE_LABELS[q.name] || q.name}
                  </span>
                  <span className={`flex items-center gap-1 text-xs ${health.color}`}>
                    <HealthIcon className="h-3.5 w-3.5" />
                    {health.label}
                  </span>
                </GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent className="pt-0">
                <div className="grid grid-cols-5 gap-1.5 text-center">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-1.5">
                    <p className="text-base font-bold text-blue-600">{q.counts?.active || 0}</p>
                    <p className="text-[9px] text-muted-foreground">Attivi</p>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded p-1.5">
                    <p className="text-base font-bold text-yellow-600">{q.counts?.waiting || 0}</p>
                    <p className="text-[9px] text-muted-foreground">Attesa</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded p-1.5">
                    <p className="text-base font-bold text-green-600">{q.counts?.completed || 0}</p>
                    <p className="text-[9px] text-muted-foreground">Completati</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded p-1.5">
                    <p className="text-base font-bold text-red-600">{q.counts?.failed || 0}</p>
                    <p className="text-[9px] text-muted-foreground">Falliti</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded p-1.5">
                    <p className="text-base font-bold text-purple-600">{q.counts?.delayed || 0}</p>
                    <p className="text-[9px] text-muted-foreground">Ritardati</p>
                  </div>
                </div>
                <div className="flex gap-1 mt-3">
                  {q.isPaused ? (
                    <Button size="sm" variant="outline" className="flex-1 text-xs h-7"
                      disabled={actionLoading !== null}
                      onClick={(e) => { e.stopPropagation(); void executeAction(q.name, 'resume'); }}>
                      <Play className="h-3 w-3 mr-1" /> Riprendi
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="flex-1 text-xs h-7"
                      disabled={actionLoading !== null}
                      onClick={(e) => { e.stopPropagation(); void executeAction(q.name, 'pause'); }}>
                      <Pause className="h-3 w-3 mr-1" /> Pausa
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="flex-1 text-xs h-7 text-orange-600"
                    disabled={actionLoading !== null || !(q.counts?.failed > 0)}
                    onClick={(e) => { e.stopPropagation(); void executeAction(q.name, 'retry'); }}>
                    <RotateCcw className="h-3 w-3 mr-1" /> Retry Tutti
                  </Button>
                </div>
              </GlassCardContent>
            </GlassCard>
          );
        })}
      </div>

      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-purple-500" />
            Throughput Code
          </GlassCardTitle>
          <div className="flex gap-2">
            <Select value={historyHours} onValueChange={setHistoryHours}>
              <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="6">6 ore</SelectItem>
                <SelectItem value="12">12 ore</SelectItem>
                <SelectItem value="24">24 ore</SelectItem>
                <SelectItem value="48">48 ore</SelectItem>
                <SelectItem value="168">7 giorni</SelectItem>
                <SelectItem value="720">30 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: 'workflows', label: 'Workflow Automazioni' },
              { key: 'mass-outreach', label: 'Invii Massivi SMS/WA' },
              { key: 'messaging', label: 'Messaggi Singoli' },
            ].map(({ key, label }) => (
              <div key={key}>
                <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
                {(allHistory[key] || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={170}>
                    <AreaChart data={allHistory[key]}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Area type="monotone" dataKey="completed" name="Completati" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} />
                      <Area type="monotone" dataKey="failed" name="Falliti" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[170px] text-muted-foreground">
                    <p className="text-xs">Nessun dato disponibile</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </GlassCardContent>
      </GlassCard>

      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle className="text-sm flex items-center gap-2">
            <ListX className="h-4 w-4 text-blue-500" />
            Job - {QUEUE_LABELS[selectedQueue] || selectedQueue}
            <Badge variant="secondary" className="text-xs">{jobsTotal} totali</Badge>
          </GlassCardTitle>
          <div className="flex gap-2 flex-wrap">
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="failed">Falliti</SelectItem>
                <SelectItem value="waiting">In Attesa</SelectItem>
                <SelectItem value="active">Attivi</SelectItem>
                <SelectItem value="completed">Completati</SelectItem>
                <SelectItem value="delayed">Ritardati</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tenantFilter} onValueChange={setTenantFilter}>
              <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Tutti i tenant" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tenant</SelectItem>
                {tenants.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="h-8 text-xs px-2 rounded border border-input bg-background" placeholder="Da" title="Data inizio" />
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="h-8 text-xs px-2 rounded border border-input bg-background" placeholder="A" title="Data fine" />
            {(dateFrom || dateTo) && (
              <Button size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground" onClick={() => { setDateFrom(''); setDateTo(''); }}>
                <X className="h-3 w-3" />
              </Button>
            )}
            {selectedStatus === 'failed' && jobs.length > 0 && (
              <>
                <Button size="sm" variant="outline" className="h-8 text-xs text-orange-600"
                  disabled={actionLoading !== null}
                  onClick={() => executeAction(selectedQueue, 'retry')}>
                  <RotateCcw className="h-3 w-3 mr-1" /> Retry Tutti
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs text-red-600"
                  disabled={actionLoading !== null}
                  onClick={() => executeAction(selectedQueue, 'clean-failed')}>
                  <Trash2 className="h-3 w-3 mr-1" /> Pulisci Falliti
                </Button>
              </>
            )}
            {selectedStatus === 'completed' && jobs.length > 0 && (
              <Button size="sm" variant="outline" className="h-8 text-xs text-muted-foreground"
                disabled={actionLoading !== null}
                onClick={() => executeAction(selectedQueue, 'clean-completed')}>
                <Trash2 className="h-3 w-3 mr-1" /> Pulisci Completati
              </Button>
            )}
            {selectedStatus === 'waiting' && jobs.length > 0 && (
              <Button size="sm" variant="outline" className="h-8 text-xs text-red-600"
                disabled={actionLoading !== null}
                onClick={() => executeAction(selectedQueue, 'drain')}>
                <SkipForward className="h-3 w-3 mr-1" /> Svuota Coda
              </Button>
            )}
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center text-muted-foreground py-10">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Nessun job {selectedStatus} in questa coda</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground text-xs">ID</th>
                    <th className="pb-2 font-medium text-muted-foreground text-xs">Tenant</th>
                    <th className="pb-2 font-medium text-muted-foreground text-xs">Tipo</th>
                    <th className="pb-2 font-medium text-muted-foreground text-xs">Destinatario</th>
                    <th className="pb-2 font-medium text-muted-foreground text-xs">Tentativi</th>
                    <th className="pb-2 font-medium text-muted-foreground text-xs">Data</th>
                    <th className="pb-2 font-medium text-muted-foreground text-xs">Stato</th>
                    <th className="pb-2 font-medium text-muted-foreground text-xs text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <>
                      <tr key={job.id} className="border-b border-dashed hover:bg-muted/50 dark:hover:bg-slate-800/50 cursor-pointer"
                        onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}>
                        <td className="py-2 font-mono text-xs text-muted-foreground">
                          {job.id?.substring(0, 8)}
                        </td>
                        <td className="py-2 text-xs">{job.data.tenantId}</td>
                        <td className="py-2">
                          <Badge variant="secondary" className="text-[10px]">
                            {job.data.channel || job.data.type || job.name || '-'}
                          </Badge>
                        </td>
                        <td className="py-2 text-xs">
                          {job.data.leadName || job.data.to || '-'}
                        </td>
                        <td className="py-2 text-xs">
                          {job.attempts}/{job.maxAttempts}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {job.timestamp ? formatDistanceToNow(new Date(job.timestamp), { addSuffix: true, locale: it }) : '-'}
                        </td>
                        <td className="py-2">
                          <Badge className={`text-[10px] ${STATUS_COLORS[job.status] || 'bg-muted text-foreground'}`}>
                            {job.status}
                          </Badge>
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex justify-end gap-1">
                            {job.status === 'failed' && (
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-orange-500"
                                disabled={actionLoading !== null}
                                onClick={(e) => { e.stopPropagation(); void executeAction(selectedQueue, 'retry', job.id); }}>
                                <RotateCcw className="h-3 w-3" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500"
                              disabled={actionLoading !== null}
                              onClick={(e) => { e.stopPropagation(); void executeAction(selectedQueue, 'remove-job', job.id); }}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                            {expandedJob === job.id ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedJob === job.id && (
                        <tr key={`${job.id}-detail`}>
                          <td colSpan={8} className="py-3 px-4 bg-muted/50 dark:bg-muted/30">
                            <div className="grid grid-cols-2 gap-4 text-xs">
                              <div>
                                <p className="font-medium text-muted-foreground mb-1">Dettagli Job</p>
                                <div className="space-y-1">
                                  <p><span className="text-muted-foreground">ID Completo:</span> {job.id}</p>
                                  <p><span className="text-muted-foreground">Campaign:</span> {job.data.campaignId || '-'}</p>
                                  <p><span className="text-muted-foreground">Canale:</span> {job.data.channel || '-'}</p>
                                  {job.processedOn && (
                                    <p><span className="text-muted-foreground">Processato:</span> {new Date(job.processedOn).toLocaleString('it-IT')}</p>
                                  )}
                                  {job.finishedOn && (
                                    <p><span className="text-muted-foreground">Terminato:</span> {new Date(job.finishedOn).toLocaleString('it-IT')}</p>
                                  )}
                                </div>
                              </div>
                              {job.failedReason && (
                                <div>
                                  <p className="font-medium text-red-600 mb-1">Errore</p>
                                  <pre className="text-xs bg-red-50 dark:bg-red-900/20 rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap">
                                    {job.failedReason}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>

      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-500 mt-0.5" />
        <div>
          <p className="font-medium text-blue-800 dark:text-blue-200">Gestione Code BullMQ</p>
          <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
            Questa dashboard monitora le 3 code principali: <strong>Workflow Automazioni</strong> (concurrency: 3),
            <strong> Invii Massivi</strong> (concurrency: 1), <strong>Messaggi Singoli</strong> (concurrency: 5).
            In caso di blocco, usa "Pausa" per fermare la coda, "Retry Tutti" per riprovare i falliti,
            o "Svuota Coda" per rimuovere i job in attesa. L'auto-refresh aggiorna ogni 10 secondi.
          </p>
        </div>
      </div>
    </div>
  );
}
